import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { Dealer } from '../dto/dealer.model';
import { authContext } from './auth.interceptor';
import { Follower } from '../dto/follower.model';
import { ListingPlan, Subscription, SubscriptionPlan } from '../dto/subscription.model';
import { DealerEmbedKey } from '../dto/dealer-embed-key.model';
import { PaymentConfirmationResponse, PaymentInitializationResponse } from '../dto/payment.model';

@Injectable({
  providedIn: 'root',
})
export class DealerService {
  httpClient = inject(HttpClient);

  getAll(): Observable<Dealer[]> {
    return this.httpClient.get<Dealer[]>(environment.apiUrl + '/dealers');
  }

  getBySlug(slug: string): Observable<Dealer> {
    return this.httpClient.get<Dealer>(environment.apiUrl + '/dealers/s/' + slug);
  }

  getWithFilter(filters?: any): Observable<Dealer[]> {
    let params = new HttpParams();

    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value != null && value !== '') {
          params = params.set(key, value);
        }
      });
    }

    return this.httpClient.get<Dealer[]>(environment.apiUrl + '/dealers', { params });
  }

  getEmbedKeys(): Observable<any[]> {
    return this.httpClient.get<any[]>(environment.apiUrl + '/dealers/me/embed-keys', { context: authContext() });
  }

  createEmbedKey(name: string, allowedOrigins: string[], expiresAt: string): Observable<DealerEmbedKey> {
    return this.httpClient.post<DealerEmbedKey>(environment.apiUrl + '/dealers/me/embed-keys', {
      "name": name,
      "allowedOrigins": allowedOrigins,
      "expiresAt": expiresAt
    }, { context: authContext() });
  }

  getMine(): Observable<Dealer> {
    return this.httpClient.get<Dealer>(environment.apiUrl + '/dealers/me', { context: authContext() });
  }

  putMine(user: Partial<Dealer>): Observable<Dealer> {
    return this.httpClient.put<Dealer>(environment.apiUrl + '/dealers/me', user, { context: authContext() });
  }

  getMyFollowers(): Observable<Follower[]> {
    return this.httpClient.get<Follower[]>(environment.apiUrl + '/dealers/me/followers', { context: authContext() });
  }

  followDealer(dealerId: number): Observable<void> {
    return this.httpClient.post<void>(environment.apiUrl + `/dealers/${dealerId}/follow/`, null, { context: authContext() });
  }

  unfollowDealer(dealerId: number): Observable<void> {
    return this.httpClient.delete<void>(environment.apiUrl + `/dealers/${dealerId}/follow/`, { context: authContext() });
  }

  // Logo
  uploadLogo(file: File): Observable<{ logoUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient.post<{ logoUrl: string }>(environment.apiUrl + '/dealers/me/logo', formData, { context: authContext() });
  }

  removeLogo(): Observable<Dealer> {
    return this.httpClient.delete<Dealer>(environment.apiUrl + '/dealers/me/logo', { context: authContext() });
  }

  // Subscriptions
  getMySubscription(): Observable<Subscription> {
    return this.httpClient.get<Subscription>(environment.apiUrl + '/dealers/me/subscription', { context: authContext() });
  }

  initializeSubscriptionPayment(plan: SubscriptionPlan, autoRenew: boolean): Observable<PaymentInitializationResponse> {
    // Remove the /api from the api url for returnUrl
    const returnUrl = environment.apiUrl.replace('/api', '') + '/dealer/subscription';
    return this.httpClient.post<PaymentInitializationResponse>(environment.apiUrl + '/payments/subscription', { plan: plan.valueOf(), autoRenew: autoRenew, returnUrl: returnUrl }, { context: authContext() });
  }

  confirmSubscriptionPayment(orderId: string): Observable<PaymentConfirmationResponse> {
    return this.httpClient.post<PaymentConfirmationResponse>(environment.apiUrl + '/payments/subscription/confirm', { orderId: orderId }, { context: authContext() });
  }

  initializeListingPayment(listingId: number, plan: ListingPlan): Observable<PaymentInitializationResponse> {
    // Remove the /api from the api url for returnUrl
    const returnUrl = environment.apiUrl.replace('/api', '') + '/dealer/listings/' + listingId + '/edit';
    return this.httpClient.post<PaymentInitializationResponse>(environment.apiUrl + '/payments/listings/' + listingId, { plan: plan.valueOf(), returnUrl: returnUrl }, { context: authContext() });
  }

  confirmListingPayment(orderId: string): Observable<PaymentConfirmationResponse> {
    return this.httpClient.post<PaymentConfirmationResponse>(environment.apiUrl + '/payments/listings/confirm', { orderId: orderId }, { context: authContext() });
  }

  cancelMySubscription(): Observable<Subscription> {
    return this.httpClient.delete<Subscription>(environment.apiUrl + '/dealers/me/subscription/auto-renew', { context: authContext() });
  }
}
